import clsx from 'clsx';
import { useMemo } from 'react';
import { differenceBy } from 'lodash';
import type { Editor } from '@tiptap/react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { NumericStepper } from '@/components/numeric-stepper';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { Popover } from '@flintmint/web-shared/components/popover';
import { Divider } from '@flintmint/web-shared/components/divider';
import { SingleSelect } from '@flintmint/web-shared/components/single-select';

import { useFormattingCommands } from '@/hooks/use-formatting-commands';
import { useElementResize } from '@flintmint/web-shared/hooks/use-element-resize';

import { toolbarGroups } from '@/constants/toolbar';
import { ptToPx } from '@flintmint/web-shared/utils/ui';
import { getVisibleFormattingGroups } from '@/utils/get-visible-formatting-groups';

import styles from './toolbar.module.scss';

export const Toolbar = (props: ToolbarProps) => {
  const { editor, streaming, processing } = props;

  const { commands, states } = useFormattingCommands(editor);

  const formattingGroups = useMemo(() => toolbarGroups(states, commands), [commands, states]);

  const { ref, clientWidth } = useElementResize();

  const visibleFormats = useMemo(() => {
    return getVisibleFormattingGroups(formattingGroups, clientWidth);
  }, [clientWidth, states, commands]);

  const moreMenuData = useMemo(() => {
    const groups = differenceBy(formattingGroups, visibleFormats, 'group');
    return groups.reduce((acc, curr, index) => {
      return [...acc, ...curr.options, ...(index !== groups.length - 1 ? [{ type: 'divider' } as any] : [])];
    }, [] as any[]);
  }, [visibleFormats, states, commands]);

  const textStylesOptions = useMemo(
    () => [
      {
        name: 'Body text',
        onClick: commands.onParagraph,
        selected: streaming || states.isParagraph,
      },
      { type: 'divider' },
      {
        name: 'Heading 1',
        onClick: commands.onHeading1,
        selected: states.isH1,
        className: styles.heading1,
      },
      {
        name: 'Heading 2',
        onClick: commands.onHeading2,
        selected: states.isH2,
        className: styles.heading2,
      },
      {
        name: 'Heading 3',
        onClick: commands.onHeading3,
        selected: states.isH3,
        className: styles.heading3,
      },
      {
        name: 'Heading 4',
        onClick: commands.onHeading4,
        selected: states.isH4,
        className: styles.heading4,
      },
      {
        name: 'Heading 5',
        onClick: commands.onHeading5,
        selected: states.isH5,
        className: styles.heading5,
      },
      {
        name: 'Heading 6',
        onClick: commands.onHeading6,
        selected: states.isH6,
        className: styles.heading6,
      },
    ],
    [streaming, states.isParagraph, states.isH1, states.isH2, states.isH3, states.isH4, states.isH5, states.isH6]
  );

  return (
    <div ref={ref} className={styles.toolbarHolder} data-testid="toolbar">
      <div
        className={clsx(styles.toolbar, 'toolbar', {
          [styles.processing]: processing,
        })}
      >
        <SingleSelect
          data-group="text-styles"
          data-testid="text-styles"
          options={streaming ? [] : textStylesOptions}
          className={styles.textStyle}
          popoverProps={{ arrow: false, placement: 'bottom' }}
        />
        <div className={styles.divider}>
          <Divider orientation="vertical" />
        </div>
        <NumericStepper
          onChange={(value) => {
            commands.onSetFontSize(value.toString());
          }}
          min={1}
          max={72}
          data-group="font-size"
          data-testid="font-size-selector"
          value={ptToPx(Number(`${states.currentSize}`.replace(/pt|px/, '')))}
        />

        {visibleFormats.map((group) => (
          <ul key={group.group} data-group={group.group}>
            <li className={styles.divider}>
              <Divider orientation="vertical" />
            </li>
            {group.options.map((el) => (
              <Tooltip key={el.name} content={el.name} shortcut={el.shortcut} placement="top">
                <li
                  onClick={el.onClick}
                  className={clsx(styles.item, {
                    [styles.selected]: el.selected,
                    [styles.disabled]: el.disabled,
                  })}
                  aria-label={el.name}
                >
                  <Icon type={el.icon} size={18} />
                  <div />
                </li>
              </Tooltip>
            ))}
          </ul>
        ))}
        {!!moreMenuData.length && (
          <Popover menu={moreMenuData} offset={[0, 0]} placement="bottom">
            <div className={styles.item} data-group="menu" data-testid="more-menu">
              <Icon type="more-horizontal" size={18} />
            </div>
          </Popover>
        )}
      </div>
    </div>
  );
};

interface ToolbarProps {
  editor: Editor;
  streaming?: boolean;
  processing?: boolean;
}
